import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    Chip,
    Divider,
    CircularProgress,
    IconButton,
    Fade
} from '@mui/material';
import {
    School,
    Visibility,
    ArrowBack,
    ArrowForward,
    Refresh,
    MenuBook,
    VolumeUp
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { userRequest } from '../../requestMethods';

const FlashcardItem = ({ word, onNext, currentIndex, totalCount, storyTitle, onGoToStory }) => {
    const [showTranslation, setShowTranslation] = useState(false);
    const [translation, setTranslation] = useState('');
    const [loadingTranslation, setLoadingTranslation] = useState(false);

    const handleToggleTranslation = async () => {
        if (!showTranslation && !translation) {
            // Fetch translation when revealing for the first time
            setLoadingTranslation(true);
            try {
                const response = await userRequest.get(`/progress-tracks/word/${word.textId}/${word.id}/translation`);
                setTranslation(response.data.t);
            } catch (error) {
                console.error('Error fetching translation:', error);
                setTranslation('Translation not available');
            } finally {
                setLoadingTranslation(false);
            }
        }
        setShowTranslation(!showTranslation);
    };

    const handleNextWord = () => {
        setShowTranslation(false);
        setTranslation(''); // Reset translation for next word
        onNext();
    };

    const handlePlayAudio = () => {
        const utterance = new SpeechSynthesisUtterance(word.w);
        utterance.rate = 0.85; // Slow down the speech rate (1.0 is normal speed, lower is slower)
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
    };

    return (
        <Card sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
            <CardContent sx={{ p: 4 }}>
                {/* Header with progress */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Chip 
                        label={`${currentIndex + 1} / ${totalCount}`}
                        color="primary"
                        variant="outlined"
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<MenuBook />}
                        onClick={onGoToStory}
                        sx={{ textTransform: 'none' }}
                    >
                        {storyTitle}
                    </Button>
                </Box>

                {/* Word Display */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Typography 
                            variant="h3" 
                            color="primary" 
                            fontWeight="bold"
                        >
                            {word.pv ? word.w : word.w}
                        </Typography>
                        <IconButton 
                            onClick={handlePlayAudio} 
                            size="large"
                            aria-label="Play pronunciation"
                            sx={{ color: 'primary.main' }}
                        >
                            <VolumeUp fontSize="large" />
                        </IconButton>
                    </Box>
                    
                                        {word.p && (
                        <Typography 
                            variant="h6" 
                            color="text.secondary" 
                            fontStyle="italic"
                        >
                            {word.p} {word.pv && ', phrase'}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Translation Section - Flip style */}
                <Box 
                    sx={{ 
                        textAlign: 'center', 
                        mb: 4,
                        minHeight: 150,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: showTranslation ? 'success.main' : 'primary.main',
                        backgroundColor: showTranslation ? 'rgba(76, 175, 80, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 3
                        }
                    }}
                    onClick={handleToggleTranslation}
                >
                    <Fade in={!showTranslation} timeout={300}>
                        <Box sx={{ display: showTranslation ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" color="primary.dark">
                                Do you know this word's meaning?
                            </Typography>
                            <Visibility sx={{ fontSize: 40, color: 'primary.main' }} />
                            <Typography variant="body1" color="primary.dark">
                                Click to reveal
                            </Typography>
                        </Box>
                    </Fade>
                    
                    <Fade in={showTranslation} timeout={300}>
                        <Box sx={{ display: showTranslation ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            {loadingTranslation ? (
                                <CircularProgress size={40} />
                            ) : (
                                <>
                                    <Typography 
                                        variant="h4" 
                                        color="success.dark"
                                        fontWeight="500"
                                    >
                                        {translation}
                                    </Typography>
                                    <Typography variant="body2" color="success.dark">
                                        Click to hide
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Fade>
                </Box>

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={handleNextWord}
                        size="large"
                        sx={{ 
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            px: 4,
                            py: 1.5
                        }}
                    >
                        Next
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

const Flashcards = () => {
    const navigate = useNavigate();
    const { storyId } = useParams();
    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [storyTitle, setStoryTitle] = useState('');

    useEffect(() => {
        if (storyId) {
            fetchWordsForStory();
        } else {
            fetchAllWords();
        }
    }, [storyId]);

    const fetchWordsForStory = async () => {
        try {
            setLoading(true);
            const res = await userRequest.get(`/progress-tracks/story/${storyId}/words`);
            
            // Add textId, storyId, and storyTitle to each word from the response level
            const wordsWithContext = res.data.words.map(word => ({
                ...word,
                textId: res.data.textId,
                storyId: res.data.storyId,
                storyTitle: res.data.storyTitle
            }));
            
            setWords(wordsWithContext);
            setStoryTitle(res.data.storyTitle);
        } catch (error) {
            console.error('Error fetching words for story:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllWords = async () => {
        try {
            setLoading(true);
            const res = await userRequest.get('/progress-tracks?page=1&limit=100');
            const tracks = res.data.progressTracks;
            
            // Combine all words and phrases from all tracks - note: these come from non-obfuscated APIs
            // We need to convert them to obfuscated format for consistency
            const combinedWords = [];
            tracks.forEach(track => {
                track.words.forEach(word => {
                    // Note: track.words comes from /progress-tracks endpoint which returns {id, w}
                    // so we just need to add the missing properties for flashcard compatibility
                    combinedWords.push({
                        id: word.id,
                        w: word.w,        // w = word content
                        pv: false,        // pv = isPhrasalVerb
                        storyTitle: track.shortStory.title,
                        storyId: track.shortStory._id,
                    });
                });
                track.phrases.forEach(phrase => {
                    // Note: track.phrases comes from /progress-tracks endpoint which returns {id, w}
                    combinedWords.push({
                        id: phrase.id,
                        w: phrase.w,      // w = phrase content  
                        pv: true,         // pv = isPhrasalVerb
                        storyTitle: track.shortStory.title,
                        storyId: track.shortStory._id,
                    });
                });
            });
            
            setWords(combinedWords);
            setStoryTitle('All Stories');
        } catch (error) {
            console.error('Error fetching all words:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNextWord = () => {
        if (currentWordIndex < words.length - 1) {
            setCurrentWordIndex(currentWordIndex + 1);
        } else {
            // Restart from beginning
            setCurrentWordIndex(0);
        }
    };

    const handleGoToStory = () => {
        const currentWord = words[currentWordIndex];
        if (currentWord && currentWord.storyId) {
            navigate(`/story/${currentWord.storyId}`);
        }
    };

    const handleBackToProfile = () => {
        navigate('/profile');
    };

    const handleRefresh = () => {
        if (storyId) {
            fetchWordsForStory();
        } else {
            fetchAllWords();
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Loading your words...
                </Typography>
            </Container>
        );
    }

    if (words.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <School color="disabled" sx={{ fontSize: 100, mb: 3 }} />
                <Typography variant="h4" gutterBottom>
                    No new words
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Read stories and tap on words to practice them here!
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={handleBackToProfile}
                >
                    Back to Profile
                </Button>
            </Container>
        );
    }

    const currentWord = words[currentWordIndex];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={handleBackToProfile} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" fontWeight="bold" color="primary">
                    Check your words
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                        label={`${words.length} words`}
                        color="info"
                        variant="outlined"
                    />
                    <IconButton onClick={handleRefresh} title="Refresh">
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {/* Flashcard */}
            {currentWord && (
                <FlashcardItem
                    word={currentWord}
                    onNext={handleNextWord}
                    currentIndex={currentWordIndex}
                    totalCount={words.length}
                    storyTitle={currentWord.storyTitle || storyTitle}
                    onGoToStory={handleGoToStory}
                />
            )}

            {/* Progress indicator */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {currentWordIndex + 1} of {words.length} words
                </Typography>
            </Box>
        </Container>
    );
};

export default Flashcards; 